import { useState, useCallback, useRef } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import {
  ArrowLeft,
  ArrowRight,
  Upload,
  FileText,
  FileSpreadsheet,
  Presentation,
  Mail,
  Check,
  X,
  AlertCircle,
  Tag,
  Variable,
  Eye,
  Settings2,
  Loader2,
  Download,
  Upload as UploadIcon,
  Sparkles,
  Plus,
  Trash2,
  Copy,
  FileJson,
  FileSpreadsheet as FileCsv,
} from 'lucide-react'
import {
  templateService,
  type TemplateVariableOData,
  type AnalyzeTemplateResponse,
  type TemplateVariableResponse,
} from '@/lib/api/services/templates'
import { useToast } from '@/components/ui/use-toast'
import { FluentCard } from '@/components/layout/page-header'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

type TemplateType = 'Word' | 'Excel' | 'PowerPoint' | 'Outlook'

interface WizardState {
  file: File | null
  name: string
  description: string
  category: string
  tags: string[]
  variables: TemplateVariableOData[]
  detectedType: TemplateType | null
  preview: string | null
  pageCount: number | null
  documentTitle: string | null
  documentAuthor: string | null
}

const STEPS = [
  { id: 'upload', label: 'Upload', icon: Upload },
  { id: 'preview', label: 'Preview', icon: Eye },
  { id: 'variables', label: 'Variables', icon: Variable },
  { id: 'metadata', label: 'Metadata', icon: Settings2 },
  { id: 'review', label: 'Review', icon: Check },
] as const

type StepId = (typeof STEPS)[number]['id']

const FILE_TYPES: Record<string, { type: TemplateType; icon: typeof FileText; color: string; extensions: string[] }> = {
  Word: {
    type: 'Word',
    icon: FileText,
    color: 'from-blue-400 to-blue-600',
    extensions: ['.docx', '.doc', '.dotx', '.dot'],
  },
  Excel: {
    type: 'Excel',
    icon: FileSpreadsheet,
    color: 'from-green-400 to-green-600',
    extensions: ['.xlsx', '.xls', '.xltx', '.xlt'],
  },
  PowerPoint: {
    type: 'PowerPoint',
    icon: Presentation,
    color: 'from-orange-400 to-orange-600',
    extensions: ['.pptx', '.ppt', '.potx', '.pot'],
  },
  Outlook: {
    type: 'Outlook',
    icon: Mail,
    color: 'from-cyan-400 to-cyan-600',
    extensions: ['.msg', '.oft', '.eml'],
  },
}

const ACCEPTED_EXTENSIONS = Object.values(FILE_TYPES).flatMap((t) => t.extensions)

function getFileType(filename: string): TemplateType | null {
  const ext = '.' + filename.split('.').pop()?.toLowerCase()
  for (const [type, config] of Object.entries(FILE_TYPES)) {
    if (config.extensions.includes(ext)) {
      return type as TemplateType
    }
  }
  return null
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(2) + ' MB'
}

export default function NewTemplatePage() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const [currentStep, setCurrentStep] = useState<StepId>('upload')
  const [loading, setLoading] = useState(false)
  const [analyzing, setAnalyzing] = useState(false)
  const [dragActive, setDragActive] = useState(false)
  const [newTag, setNewTag] = useState('')

  const [state, setState] = useState<WizardState>({
    file: null,
    name: '',
    description: '',
    category: '',
    tags: [],
    variables: [],
    detectedType: null,
    preview: null,
    pageCount: null,
    documentTitle: null,
    documentAuthor: null,
  })

  const currentStepIndex = STEPS.findIndex((s) => s.id === currentStep)

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    const files = e.dataTransfer.files
    if (files && files[0]) {
      handleFileSelect(files[0])
    }
  }, [])

  const handleFileSelect = async (file: File) => {
    const type = getFileType(file.name)
    if (!type) {
      toast({
        title: 'Invalid file type',
        description: 'Please upload a Word, Excel, PowerPoint, or Outlook file.',
        variant: 'destructive',
      })
      return
    }

    // Set file and auto-fill name from filename
    const baseName = file.name.replace(/\.[^/.]+$/, '')
    setState((prev) => ({
      ...prev,
      file,
      name: baseName,
      detectedType: type,
    }))

    // Analyze the template
    setAnalyzing(true)
    try {
      const analysis: AnalyzeTemplateResponse = await templateService.analyzeTemplate(file)

      // Map TemplateVariableResponse to TemplateVariableOData
      const mappedVariables: TemplateVariableOData[] = (analysis.variables || []).map(
        (v: TemplateVariableResponse) => ({
          name: v.name,
          displayName: v.displayName,
          type: v.type,
          required: v.required,
          defaultValue: v.defaultValue,
          format: v.format,
        })
      )

      setState((prev) => ({
        ...prev,
        variables: mappedVariables,
        preview: analysis.thumbnailUrl || null,
        pageCount: analysis.pageCount || null,
        documentTitle: analysis.title || null,
        documentAuthor: analysis.author || null,
        // Use document title as template name if available and current name is just the filename
        name: analysis.title && prev.name === baseName ? analysis.title : prev.name,
      }))
    } catch (_error) {
      // Analysis is optional - continue without it
      console.warn('Template analysis failed, continuing without detected variables')
    } finally {
      setAnalyzing(false)
    }

    // Move to preview step
    setCurrentStep('preview')
  }

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      handleFileSelect(file)
    }
  }, [])

  const handleRemoveFile = () => {
    setState({
      file: null,
      name: '',
      description: '',
      category: '',
      tags: [],
      variables: [],
      detectedType: null,
      preview: null,
      pageCount: null,
      documentTitle: null,
      documentAuthor: null,
    })
    setCurrentStep('upload')
  }

  const handleAddTag = () => {
    if (newTag.trim() && !state.tags.includes(newTag.trim())) {
      setState((prev) => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()],
      }))
      setNewTag('')
    }
  }

  const handleRemoveTag = (tag: string) => {
    setState((prev) => ({
      ...prev,
      tags: prev.tags.filter((t) => t !== tag),
    }))
  }

  const handleVariableChange = (index: number, field: keyof TemplateVariableOData, value: string | boolean) => {
    setState((prev) => {
      const newVariables = [...prev.variables]
      const currentVariable = newVariables[index]
      if (currentVariable) {
        newVariables[index] = {
          ...currentVariable,
          [field]: value,
        }
      }
      return { ...prev, variables: newVariables }
    })
  }

  const handleAddVariable = () => {
    setState((prev) => ({
      ...prev,
      variables: [
        ...prev.variables,
        {
          name: `variable_${prev.variables.length + 1}`,
          displayName: `Variable ${prev.variables.length + 1}`,
          type: 'Text' as const,
          required: false,
        },
      ],
    }))
  }

  const handleRemoveVariable = (index: number) => {
    setState((prev) => ({
      ...prev,
      variables: prev.variables.filter((_, i) => i !== index),
    }))
  }

  // Import/Export refs
  const importInputRef = useRef<HTMLInputElement>(null)

  const handleExportVariables = (format: 'json' | 'csv') => {
    if (state.variables.length === 0) {
      toast({
        title: 'No variables to export',
        description: 'Add some variables first before exporting.',
        variant: 'destructive',
      })
      return
    }

    let content: string
    let filename: string
    let mimeType: string

    if (format === 'json') {
      content = JSON.stringify(state.variables, null, 2)
      filename = `${state.name || 'template'}-variables.json`
      mimeType = 'application/json'
    } else {
      // CSV format
      const headers = ['name', 'displayName', 'type', 'required', 'defaultValue', 'format']
      const rows = state.variables.map((v) => [
        v.name,
        v.displayName,
        v.type,
        v.required ? 'true' : 'false',
        v.defaultValue?.toString() || '',
        v.format || '',
      ])
      content = [headers.join(','), ...rows.map((r) => r.map((c) => `"${c}"`).join(','))].join('\n')
      filename = `${state.name || 'template'}-variables.csv`
      mimeType = 'text/csv'
    }

    const blob = new Blob([content], { type: mimeType })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)

    toast({
      title: 'Variables exported',
      description: `Exported ${state.variables.length} variables to ${format.toUpperCase()}.`,
    })
  }

  const handleImportVariables = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (event) => {
      try {
        const content = event.target?.result as string
        let importedVariables: TemplateVariableOData[] = []

        if (file.name.endsWith('.json')) {
          const parsed = JSON.parse(content)
          if (Array.isArray(parsed)) {
            importedVariables = parsed.map((v) => ({
              name: v.name || `variable_${Date.now()}`,
              displayName: v.displayName || v.name || 'Variable',
              type: v.type || 'Text',
              required: v.required || false,
              defaultValue: v.defaultValue,
              format: v.format,
            }))
          }
        } else if (file.name.endsWith('.csv')) {
          const lines = content.split('\n').filter((l) => l.trim())
          const headerLine = lines[0]
          if (lines.length > 1 && headerLine) {
            const headers = headerLine.split(',').map((h) => h.replace(/"/g, '').trim())
            for (let i = 1; i < lines.length; i++) {
              const line = lines[i]
              if (!line) continue
              const values = line.split(',').map((v) => v.replace(/"/g, '').trim())
              const variable: TemplateVariableOData = {
                name: values[headers.indexOf('name')] || `variable_${i}`,
                displayName: values[headers.indexOf('displayName')] || values[headers.indexOf('name')] || `Variable ${i}`,
                type: (values[headers.indexOf('type')] as TemplateVariableOData['type']) || 'Text',
                required: values[headers.indexOf('required')]?.toLowerCase() === 'true',
                defaultValue: values[headers.indexOf('defaultValue')] || undefined,
                format: values[headers.indexOf('format')] || undefined,
              }
              importedVariables.push(variable)
            }
          }
        }

        if (importedVariables.length > 0) {
          setState((prev) => ({
            ...prev,
            variables: [...prev.variables, ...importedVariables],
          }))
          toast({
            title: 'Variables imported',
            description: `Imported ${importedVariables.length} variables.`,
          })
        } else {
          toast({
            title: 'Import failed',
            description: 'No valid variables found in the file.',
            variant: 'destructive',
          })
        }
      } catch (_error) {
        toast({
          title: 'Import failed',
          description: 'Could not parse the file. Please check the format.',
          variant: 'destructive',
        })
      }
    }
    reader.readAsText(file)

    // Reset input
    if (importInputRef.current) {
      importInputRef.current.value = ''
    }
  }

  const handleCopyVariableName = (name: string) => {
    navigator.clipboard.writeText(`{{${name}}}`)
    toast({
      title: 'Copied!',
      description: `{{${name}}} copied to clipboard.`,
    })
  }

  const handleSubmit = async () => {
    if (!state.file) {
      toast({
        title: 'Error',
        description: 'Please upload a template file',
        variant: 'destructive',
      })
      return
    }

    if (!state.name.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter a template name',
        variant: 'destructive',
      })
      return
    }

    try {
      setLoading(true)
      const template = await templateService.uploadTemplate({
        file: state.file,
        name: state.name.trim(),
        description: state.description.trim() || undefined,
        category: state.category.trim() || undefined,
        tags: state.tags.length > 0 ? state.tags : undefined,
        variables: state.variables.length > 0 ? state.variables : undefined,
      })

      toast({
        title: 'Success',
        description: 'Template created successfully',
      })
      navigate(`/templates/${template.id}`)
    } catch (_error) {
      toast({
        title: 'Error',
        description: 'Failed to create template',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }

  const canProceed = (): boolean => {
    switch (currentStep) {
      case 'upload':
        return !!state.file
      case 'preview':
        return true
      case 'variables':
        return true
      case 'metadata':
        return !!state.name.trim()
      case 'review':
        return true
      default:
        return false
    }
  }

  const goToStep = (step: StepId) => {
    const stepIndex = STEPS.findIndex((s) => s.id === step)
    if (stepIndex <= currentStepIndex || canProceed()) {
      setCurrentStep(step)
    }
  }

  const nextStep = () => {
    const nextStepData = STEPS[currentStepIndex + 1]
    if (currentStepIndex < STEPS.length - 1 && canProceed() && nextStepData) {
      setCurrentStep(nextStepData.id)
    }
  }

  const prevStep = () => {
    const prevStepData = STEPS[currentStepIndex - 1]
    if (currentStepIndex > 0 && prevStepData) {
      setCurrentStep(prevStepData.id)
    }
  }

  const typeConfig = state.detectedType ? FILE_TYPES[state.detectedType] : null
  const TypeIcon = typeConfig?.icon || FileText

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Back Button */}
      <Link
        to="/templates"
        className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Templates
      </Link>

      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">Upload Template</h1>
        <p className="text-neutral-500 dark:text-neutral-400 mt-1">
          Upload an Office document to create a new template
        </p>
      </div>

      {/* Step Indicator */}
      <div className="flex items-center justify-between">
        {STEPS.map((step, index) => {
          const StepIcon = step.icon
          const isActive = step.id === currentStep
          const isCompleted = index < currentStepIndex
          const isClickable = index <= currentStepIndex || (index === currentStepIndex + 1 && canProceed())

          return (
            <div key={step.id} className="flex items-center">
              <button
                onClick={() => isClickable && goToStep(step.id)}
                disabled={!isClickable}
                className={cn(
                  'flex items-center gap-2 px-3 py-2 rounded-lg transition-colors',
                  isActive && 'bg-neutral-900 dark:bg-white text-white dark:text-neutral-900',
                  isCompleted && !isActive && 'text-green-600 dark:text-green-400',
                  !isActive && !isCompleted && 'text-neutral-400 dark:text-neutral-500',
                  isClickable && !isActive && 'hover:bg-neutral-100 dark:hover:bg-neutral-800 cursor-pointer',
                  !isClickable && 'cursor-not-allowed'
                )}
              >
                {isCompleted && !isActive ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <StepIcon className="h-4 w-4" />
                )}
                <span className="text-sm font-medium hidden sm:inline">{step.label}</span>
              </button>
              {index < STEPS.length - 1 && (
                <div
                  className={cn(
                    'w-8 lg:w-16 h-px mx-2',
                    index < currentStepIndex ? 'bg-green-500' : 'bg-neutral-200 dark:bg-neutral-700'
                  )}
                />
              )}
            </div>
          )
        })}
      </div>

      {/* Step Content */}
      <FluentCard className="min-h-[400px]">
        {/* Upload Step */}
        {currentStep === 'upload' && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
                Upload your template file
              </h2>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
                Supports Word, Excel, PowerPoint, and Outlook files
              </p>
            </div>

            <div
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              className={cn(
                'relative border-2 border-dashed rounded-2xl p-12 transition-all text-center',
                dragActive
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/20'
                  : 'border-neutral-200 dark:border-neutral-700 hover:border-neutral-300 dark:hover:border-neutral-600'
              )}
            >
              <input
                type="file"
                id="template-upload"
                className="hidden"
                accept={ACCEPTED_EXTENSIONS.join(',')}
                onChange={handleInputChange}
              />

              <Upload className="h-12 w-12 mx-auto text-neutral-400 dark:text-neutral-500 mb-4" />

              <p className="text-neutral-900 dark:text-white font-medium mb-2">
                Drag and drop your file here
              </p>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-4">or</p>

              <label
                htmlFor="template-upload"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 text-sm font-semibold rounded-full hover:bg-neutral-800 dark:hover:bg-neutral-100 transition-colors cursor-pointer"
              >
                Browse Files
              </label>

              <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
                {Object.entries(FILE_TYPES).map(([type, config]) => {
                  const Icon = config.icon
                  return (
                    <div
                      key={type}
                      className="flex items-center gap-2 text-sm text-neutral-500 dark:text-neutral-400"
                    >
                      <div className={cn('p-1.5 rounded-lg bg-gradient-to-br', config.color)}>
                        <Icon className="h-3.5 w-3.5 text-white" />
                      </div>
                      <span>{type}</span>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        )}

        {/* Preview Step */}
        {currentStep === 'preview' && state.file && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
                Template Preview
              </h2>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
                Review your uploaded template
              </p>
            </div>

            {analyzing ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-neutral-400" />
                <span className="ml-3 text-neutral-500">Analyzing template...</span>
              </div>
            ) : (
              <div className="flex flex-col md:flex-row gap-6">
                {/* Preview Image or Placeholder */}
                <div className="flex-shrink-0 w-full md:w-64">
                  <div className="aspect-[3/4] rounded-xl bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center overflow-hidden">
                    {state.preview ? (
                      <img
                        src={state.preview}
                        alt="Template preview"
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <div className={cn('p-6 rounded-2xl bg-gradient-to-br', typeConfig?.color)}>
                        <TypeIcon className="h-16 w-16 text-white" />
                      </div>
                    )}
                  </div>
                </div>

                {/* File Info */}
                <div className="flex-1 space-y-4">
                  <div className="flex items-start gap-3">
                    <div className={cn('p-2.5 rounded-xl bg-gradient-to-br flex-shrink-0', typeConfig?.color)}>
                      <TypeIcon className="h-5 w-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-neutral-900 dark:text-white truncate">
                        {state.file.name}
                      </h3>
                      <p className="text-sm text-neutral-500 dark:text-neutral-400">
                        {formatFileSize(state.file.size)}
                        {state.pageCount && ` • ${state.pageCount} page${state.pageCount > 1 ? 's' : ''}`}
                      </p>
                    </div>
                    <button
                      onClick={handleRemoveFile}
                      className="p-2 rounded-lg text-neutral-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/20 transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="pt-4 border-t border-neutral-200 dark:border-neutral-700">
                    <h4 className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-3">
                      File Details
                    </h4>
                    <dl className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <dt className="text-neutral-500 dark:text-neutral-400">Type</dt>
                        <dd className="text-neutral-900 dark:text-white font-medium">
                          {state.detectedType} Document
                        </dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-neutral-500 dark:text-neutral-400">Size</dt>
                        <dd className="text-neutral-900 dark:text-white font-medium">
                          {formatFileSize(state.file.size)}
                        </dd>
                      </div>
                      {state.pageCount && state.pageCount > 0 && (
                        <div className="flex justify-between">
                          <dt className="text-neutral-500 dark:text-neutral-400">Pages</dt>
                          <dd className="text-neutral-900 dark:text-white font-medium">
                            {state.pageCount}
                          </dd>
                        </div>
                      )}
                      {state.documentTitle && (
                        <div className="flex justify-between">
                          <dt className="text-neutral-500 dark:text-neutral-400">Document Title</dt>
                          <dd className="text-neutral-900 dark:text-white font-medium truncate max-w-[200px]">
                            {state.documentTitle}
                          </dd>
                        </div>
                      )}
                      {state.documentAuthor && (
                        <div className="flex justify-between">
                          <dt className="text-neutral-500 dark:text-neutral-400">Author</dt>
                          <dd className="text-neutral-900 dark:text-white font-medium truncate max-w-[200px]">
                            {state.documentAuthor}
                          </dd>
                        </div>
                      )}
                      {state.variables.length > 0 && (
                        <div className="flex justify-between">
                          <dt className="text-neutral-500 dark:text-neutral-400">Variables Detected</dt>
                          <dd className="text-neutral-900 dark:text-white font-medium">
                            {state.variables.length}
                          </dd>
                        </div>
                      )}
                    </dl>
                  </div>

                  {state.variables.length > 0 && (
                    <div className="p-3 rounded-xl bg-blue-50 dark:bg-blue-950/20 text-blue-700 dark:text-blue-300 text-sm flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      <span>
                        We detected {state.variables.length} variable placeholder
                        {state.variables.length > 1 ? 's' : ''} in your template. Configure them in the next step.
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Variables Step */}
        {currentStep === 'variables' && (
          <div className="space-y-4">
            {/* Header with actions */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-neutral-900 dark:text-white flex items-center gap-2">
                  <Variable className="h-5 w-5" />
                  Variables
                  {state.variables.length > 0 && (
                    <Badge variant="secondary" className="ml-2">
                      {state.variables.length} detected
                    </Badge>
                  )}
                </h2>
                <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">
                  Configure placeholders from your template
                </p>
              </div>

              {/* Import/Export buttons */}
              <div className="flex items-center gap-2">
                <input
                  ref={importInputRef}
                  type="file"
                  accept=".json,.csv"
                  onChange={handleImportVariables}
                  className="hidden"
                  id="import-variables"
                />
                <div className="relative group">
                  <button
                    onClick={() => importInputRef.current?.click()}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-lg transition-colors"
                  >
                    <UploadIcon className="h-3.5 w-3.5" />
                    Import
                  </button>
                </div>
                <div className="relative group">
                  <button
                    disabled={state.variables.length === 0}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Download className="h-3.5 w-3.5" />
                    Export
                  </button>
                  <div className="absolute right-0 top-full mt-1 hidden group-hover:block z-10">
                    <div className="bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow-lg py-1 min-w-[120px]">
                      <button
                        onClick={() => handleExportVariables('json')}
                        className="w-full px-3 py-1.5 text-xs text-left text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 flex items-center gap-2"
                      >
                        <FileJson className="h-3.5 w-3.5" />
                        Export JSON
                      </button>
                      <button
                        onClick={() => handleExportVariables('csv')}
                        className="w-full px-3 py-1.5 text-xs text-left text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 flex items-center gap-2"
                      >
                        <FileCsv className="h-3.5 w-3.5" />
                        Export CSV
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Auto-detected notice */}
            {state.variables.length > 0 && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50 dark:bg-emerald-950/20 text-emerald-700 dark:text-emerald-300 text-xs">
                <Sparkles className="h-3.5 w-3.5 flex-shrink-0" />
                <span>
                  {state.variables.length} variable{state.variables.length > 1 ? 's' : ''} automatically parsed from your document
                </span>
              </div>
            )}

            {state.variables.length === 0 ? (
              <div className="text-center py-10">
                <Variable className="h-10 w-10 mx-auto text-neutral-300 dark:text-neutral-600 mb-3" />
                <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-3">
                  No variables detected in your template
                </p>
                <div className="flex items-center justify-center gap-2">
                  <button
                    onClick={handleAddVariable}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/20 rounded-lg transition-colors"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add Manually
                  </button>
                  <button
                    onClick={() => importInputRef.current?.click()}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-lg transition-colors"
                  >
                    <UploadIcon className="h-3.5 w-3.5" />
                    Import from File
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {/* Table header */}
                <div className="hidden sm:grid sm:grid-cols-12 gap-2 px-3 py-2 text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">
                  <div className="col-span-3">Placeholder</div>
                  <div className="col-span-3">Display Name</div>
                  <div className="col-span-2">Type</div>
                  <div className="col-span-2">Default</div>
                  <div className="col-span-1 text-center">Required</div>
                  <div className="col-span-1"></div>
                </div>

                {/* Variable rows */}
                <div className="space-y-1.5 max-h-[320px] overflow-y-auto pr-1">
                  {state.variables.map((variable, index) => (
                    <div
                      key={index}
                      className="grid grid-cols-1 sm:grid-cols-12 gap-2 p-2.5 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-neutral-50/50 dark:bg-neutral-800/50 hover:border-neutral-300 dark:hover:border-neutral-600 transition-colors items-center"
                    >
                      {/* Placeholder name */}
                      <div className="col-span-3 flex items-center gap-1.5">
                        <code className="text-xs font-mono text-neutral-600 dark:text-neutral-400 bg-neutral-100 dark:bg-neutral-700 px-1.5 py-0.5 rounded truncate">
                          {'{{' + variable.name + '}}'}
                        </code>
                        <button
                          onClick={() => handleCopyVariableName(variable.name)}
                          className="p-1 rounded text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors flex-shrink-0"
                          title="Copy placeholder"
                        >
                          <Copy className="h-3 w-3" />
                        </button>
                      </div>

                      {/* Display name */}
                      <div className="col-span-3">
                        <input
                          type="text"
                          value={variable.displayName}
                          onChange={(e) => handleVariableChange(index, 'displayName', e.target.value)}
                          placeholder="Display name"
                          className="w-full h-7 px-2 text-xs rounded border border-neutral-200 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-neutral-400"
                        />
                      </div>

                      {/* Type */}
                      <div className="col-span-2">
                        <select
                          value={variable.type}
                          onChange={(e) => handleVariableChange(index, 'type', e.target.value)}
                          className="w-full h-7 px-2 text-xs rounded border border-neutral-200 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-neutral-400"
                        >
                          <option value="Text">Text</option>
                          <option value="Number">Number</option>
                          <option value="Date">Date</option>
                          <option value="Boolean">Yes/No</option>
                          <option value="List">List</option>
                          <option value="Email">Email</option>
                          <option value="Phone">Phone</option>
                          <option value="Currency">Currency</option>
                          <option value="Percent">Percent</option>
                          <option value="Url">URL</option>
                          <option value="Image">Image</option>
                        </select>
                      </div>

                      {/* Default value */}
                      <div className="col-span-2">
                        <input
                          type="text"
                          value={variable.defaultValue?.toString() || ''}
                          onChange={(e) => handleVariableChange(index, 'defaultValue', e.target.value)}
                          placeholder="Default"
                          className="w-full h-7 px-2 text-xs rounded border border-neutral-200 dark:border-neutral-600 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-neutral-400"
                        />
                      </div>

                      {/* Required checkbox */}
                      <div className="col-span-1 flex justify-center">
                        <input
                          type="checkbox"
                          checked={variable.required}
                          onChange={(e) => handleVariableChange(index, 'required', e.target.checked)}
                          className="h-4 w-4 rounded border-neutral-300 dark:border-neutral-600 text-neutral-900 focus:ring-neutral-400"
                        />
                      </div>

                      {/* Delete button */}
                      <div className="col-span-1 flex justify-end">
                        <button
                          onClick={() => handleRemoveVariable(index)}
                          className="p-1.5 rounded text-neutral-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/20 transition-colors"
                          title="Remove variable"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Add variable button */}
                <button
                  onClick={handleAddVariable}
                  className="w-full p-2 rounded-lg border border-dashed border-neutral-300 dark:border-neutral-600 text-neutral-500 dark:text-neutral-400 hover:border-neutral-400 dark:hover:border-neutral-500 hover:text-neutral-600 dark:hover:text-neutral-300 transition-colors text-xs font-medium flex items-center justify-center gap-1.5"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add Variable
                </button>

                {/* Quick tip */}
                <p className="text-[10px] text-neutral-400 dark:text-neutral-500 text-center pt-1">
                  Tip: Import variables from JSON/CSV to quickly populate from existing configurations
                </p>
              </div>
            )}
          </div>
        )}

        {/* Metadata Step */}
        {currentStep === 'metadata' && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
                Template Details
              </h2>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
                Add information to help organize and find your template
              </p>
            </div>

            <div className="space-y-5 max-w-xl mx-auto">
              <div className="space-y-2">
                <label htmlFor="name" className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                  Template Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="name"
                  type="text"
                  placeholder="Enter template name"
                  value={state.name}
                  onChange={(e) => setState((prev) => ({ ...prev, name: e.target.value }))}
                  required
                  className="w-full h-11 px-4 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-neutral-900 dark:focus:ring-white transition-all"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="description" className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                  Description
                </label>
                <textarea
                  id="description"
                  placeholder="Describe what this template is used for"
                  value={state.description}
                  onChange={(e) => setState((prev) => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-neutral-900 dark:focus:ring-white transition-all resize-none"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="category" className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                  Category
                </label>
                <input
                  id="category"
                  type="text"
                  placeholder="e.g., Sales, Legal, HR, Marketing"
                  value={state.category}
                  onChange={(e) => setState((prev) => ({ ...prev, category: e.target.value }))}
                  className="w-full h-11 px-4 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-neutral-900 dark:focus:ring-white transition-all"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-neutral-700 dark:text-neutral-300">Tags</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Add a tag"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                    className="flex-1 h-11 px-4 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-neutral-900 dark:focus:ring-white transition-all"
                  />
                  <button
                    type="button"
                    onClick={handleAddTag}
                    className="px-4 h-11 rounded-xl bg-neutral-100 dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-neutral-700 transition-colors text-sm font-medium"
                  >
                    Add
                  </button>
                </div>
                {state.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {state.tags.map((tag) => (
                      <Badge key={tag} variant="secondary" className="gap-1">
                        <Tag className="h-3 w-3" />
                        {tag}
                        <button
                          onClick={() => handleRemoveTag(tag)}
                          className="ml-1 hover:text-red-500 transition-colors"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Review Step */}
        {currentStep === 'review' && state.file && (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h2 className="text-lg font-semibold text-neutral-900 dark:text-white">
                Review & Publish
              </h2>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
                Review your template before publishing
              </p>
            </div>

            <div className="max-w-xl mx-auto space-y-6">
              {/* Template Summary */}
              <div className="flex items-start gap-4 p-4 rounded-xl bg-neutral-50 dark:bg-neutral-800/50">
                <div className={cn('p-3 rounded-xl bg-gradient-to-br flex-shrink-0', typeConfig?.color)}>
                  <TypeIcon className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-neutral-900 dark:text-white">{state.name}</h3>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400 truncate">
                    {state.file.name}
                  </p>
                  {state.description && (
                    <p className="text-sm text-neutral-600 dark:text-neutral-300 mt-2">{state.description}</p>
                  )}
                </div>
              </div>

              {/* Details */}
              <div className="divide-y divide-neutral-200 dark:divide-neutral-700">
                <div className="py-3 flex justify-between">
                  <span className="text-sm text-neutral-500 dark:text-neutral-400">Type</span>
                  <span className="text-sm font-medium text-neutral-900 dark:text-white">
                    {state.detectedType}
                  </span>
                </div>
                <div className="py-3 flex justify-between">
                  <span className="text-sm text-neutral-500 dark:text-neutral-400">File Size</span>
                  <span className="text-sm font-medium text-neutral-900 dark:text-white">
                    {formatFileSize(state.file.size)}
                  </span>
                </div>
                {state.category && (
                  <div className="py-3 flex justify-between">
                    <span className="text-sm text-neutral-500 dark:text-neutral-400">Category</span>
                    <span className="text-sm font-medium text-neutral-900 dark:text-white">
                      {state.category}
                    </span>
                  </div>
                )}
                <div className="py-3 flex justify-between">
                  <span className="text-sm text-neutral-500 dark:text-neutral-400">Variables</span>
                  <span className="text-sm font-medium text-neutral-900 dark:text-white">
                    {state.variables.length || 'None'}
                  </span>
                </div>
                {state.tags.length > 0 && (
                  <div className="py-3">
                    <span className="text-sm text-neutral-500 dark:text-neutral-400 block mb-2">Tags</span>
                    <div className="flex flex-wrap gap-2">
                      {state.tags.map((tag) => (
                        <Badge key={tag} variant="secondary">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </FluentCard>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={prevStep}
          disabled={currentStepIndex === 0}
          className={cn(
            'flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-colors',
            currentStepIndex === 0
              ? 'text-neutral-300 dark:text-neutral-600 cursor-not-allowed'
              : 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800'
          )}
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </button>

        <div className="flex items-center gap-3">
          <Link
            to="/templates"
            className="px-5 py-2.5 rounded-full text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
          >
            Cancel
          </Link>

          {currentStep === 'review' ? (
            <button
              onClick={handleSubmit}
              disabled={loading || !canProceed()}
              className="flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-semibold text-white bg-neutral-900 dark:bg-white dark:text-neutral-900 hover:bg-neutral-800 dark:hover:bg-neutral-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-lg shadow-neutral-900/20 dark:shadow-white/20"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4" />
                  Create Template
                </>
              )}
            </button>
          ) : (
            <button
              onClick={nextStep}
              disabled={!canProceed() || analyzing}
              className="flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-semibold text-white bg-neutral-900 dark:bg-white dark:text-neutral-900 hover:bg-neutral-800 dark:hover:bg-neutral-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-lg shadow-neutral-900/20 dark:shadow-white/20"
            >
              {analyzing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  Continue
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
